import {
  BusinessPartnerInterlocutorAttributes,
  CompanyAttributes,
  DocumentClassAttributes,
  MaterialAttributes,
  OrderAttributes,
  SubRouteAttributes,
  WarehouseAttributes,
} from "@albatrosdeveloper/ave-models-package";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  noop,
  Normalizers,
  Ops,
  orderBy,
  OrderVariant,
  page,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { AsyncPipe, CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import {
  MatOptionModule,
  provideNativeDateAdapter,
} from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatSelectModule } from "@angular/material/select";
import { MatSidenav, MatSidenavContainer } from "@angular/material/sidenav";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatRowDef, MatTableModule } from "@angular/material/table";
import { MatTabsModule } from "@angular/material/tabs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { delay, map, Observable, startWith, tap } from "rxjs";
import { ShipmentRequestInterface } from "./interfaces/create-order.interface";
import { FetchSequenceMaterialsFieldsRequest } from "./interfaces/fetch-sequence-materials-fields-request.interface";
import { MaterialExt } from "./interfaces/material-extension.interface";
import { MaterialsUMSelectionInterface } from "./interfaces/materials-um-selection-params.interface";
import { ordersResponseMock } from "./mock-data.mock";
import { OrdersCreationService } from "./orders-creation.service";

import BusinessPartnerAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity";
import SalesOfficeAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity";
import CurrencyAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/masters/currency/currency.entity";
import PaymentConditionAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/masters/payment-condition/payment-condition.entity";
import RouteAttributes from "@albatrosdeveloper/ave-models-package/src/schemas/routing/route/route.entity";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { CompanyCurrencyPipe } from "app/pipes/currency.pipe";

@Component({
  selector: "app-orders-creation",
  templateUrl: "./orders-creation.component.html",
  styleUrls: ["./orders-creation.component.scss"],
  standalone: true,
  providers: [OrdersCreationService, provideNativeDateAdapter()],
  imports: [
    CommonModule,
    MatTableModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatSlideToggle,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatMenuModule,
    MatIconModule,
    MatRowDef,
    MatSidenav,
    MatSidenavContainer,
    FormsModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    AsyncPipe,
    MatTabsModule,
    CompanyCurrencyPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersCreationComponent {
  private _snackBar = inject(MatSnackBar);

  //#region Common variables
  currentTotal: number = 0;
  currentSubTotal: number = 0;
  currentTaxTotal: number = 0;
  materialsCollapsed: boolean = true;
  orderHeadersCollapsed: boolean = false;
  currentUpdateLocation: OrderAttributes;
  currentOrderRequest: ShipmentRequestInterface = { orders: [] };
  materialsList: MaterialExt[] = [];
  orderedMaterials: MaterialExt[] = [];
  displayedColumns: string[] = [
    "product",
    "ltsLbs",
    "price",
    "quantity",
    "um",
    "ump",
    "subtotal",
  ];
  materialsUMSelection: MaterialsUMSelectionInterface[] = [];
  //#endregion

  //#region Form and controls values
  salesOfficeControl = new FormControl("");
  salesOfficesList: SalesOfficeAttributes[] = [];
  salesOffices$: Observable<SalesOfficeAttributes[]>;

  routesList: RouteAttributes[] = [];
  subRoutesList: SubRouteAttributes[] = [];
  subRouteControl = new FormControl("");
  subRoutesList$: Observable<SubRouteAttributes[]>;

  soldToList: BusinessPartnerAttributes[] = [];
  shipToList: BusinessPartnerAttributes[] = [];
  shipToControl = new FormControl("");
  filteredDocumentClasses$: Observable<DocumentClassAttributes[]>;

  orderTypeControl = new FormControl("");

  documentClassesList: DocumentClassAttributes[] = [];
  warehousesList: RouteAttributes[] = [];
  warehouseControl = new FormControl("");
  filteredWarehouses$: Observable<WarehouseAttributes[]>;

  currenciesList: CurrencyAttributes[] = [];
  currencyControl = new FormControl("");
  filteredCurrencies$: Observable<WarehouseAttributes[]>;

  chargeDateControl = new FormControl("");
  deliveryDateControl = new FormControl("");
  deliveryHourControl = new FormControl("");

  referenceControl = new FormControl("");

  comentariesControl = new FormControl("");

  paymentConditionControl = new FormControl("");
  paymentConditionsList: PaymentConditionAttributes[] = [];
  paymentConditions$: Observable<PaymentConditionAttributes[]>;

  salesForceControl = new FormControl("");
  salesForcesList: SalesOfficeAttributes[] = [];
  salesForces$: Observable<SalesOfficeAttributes[]>;
  //#endregion

  //#region TO DELETE

  //#region Mocks
  ordersResponseMock: {
    valid: Partial<OrderAttributes & any>[];
    invalid: Partial<OrderAttributes & any>[];
  } = ordersResponseMock;
  ordersFiltered: {
    invalid?: Partial<OrderAttributes & Record<string, any>>[];
    toVerify?: Partial<OrderAttributes & Record<string, any>>[];
    valid?: Partial<OrderAttributes & Record<string, any>>[];
  } = {};
  //#endregion

  //#endregion

  //#region Filtros
  selectedRouteId: string = "none";
  selectedRoute: RouteAttributes;
  selectedSalesOfficeId: string = null;
  selectedPaymentConditionId: string = null;
  selectedSalesForceId: string = null;
  selectedSubRouteId: string = null;
  selectedShipToId: string = "none";
  selectedSoldToId: string = "none";
  selectedSoldToBP: BusinessPartnerAttributes;
  selectedShipToBP: BusinessPartnerAttributes & { soldToId?: string };
  selectedSalesOffice: SalesOfficeAttributes;
  selectedCompanyBySalesOffice: CompanyAttributes;
  showFilterContainer = false;
  currentCategory = "all";
  hideCompleted = false;
  searchQuery: string = "";
  //#endregion

  //#region Paginación
  totalItems = 0;
  pageSize = 5; // Tamaño de página predeterminado
  pageIndex = 0;
  //#endregion

  @ViewChild("addModal") createOrderModal!: TemplateRef<any>;
  @ViewChild("orderVerificationModal")
  orderVerificationModal!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private ordersCreationService: OrdersCreationService
  ) {}

  ngAfterViewInit(): void {
    this.shipToControl.disable();
    this.paginator.page.subscribe(() => this.findMaterialsWithCount());
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0; // Reiniciar la página cuando cambie el ordenamiento
      this.findMaterialsWithCount();
    });
    this.subRouteControl.disable();
    //#region Llamada inicial para obtener los datos
    this.findSalesOfficeByQuery();
    this.findDocumentClassesByQuery();
    this.findCurrenciesByQuery();
    this.findMaterialsWithCount();
    this.findPaymentConditionByQuery();
    this.findSalesForceByQuery();
    //#endregion
    //! DELETE THIS Llamada mock
    this.filterOrdersToConfirm(this.ordersResponseMock);
    //#region Subscriptions
    this.salesOffices$.subscribe((response) => {
      this.salesOfficesList = response;
    });
    this.salesOfficeControl.valueChanges
      .pipe(
        tap((value) => {
          if (value.length === 24) {
            this.selectedSalesOfficeId = value;
            this.applyFilters("salesOffice");
            this.subRouteControl.enable();
          } else {
            this.selectedSalesOfficeId = null;
          }
        }),
        delay(500)
      )
      .subscribe((value) => {
        if (value.length !== 24) {
          const query = buildQuery<SalesOfficeAttributes>(
            where("name", Ops.like(value, "i"))
          );
          this.findSalesOfficeByQuery(query);
        }
      });
    this.subRouteControl.valueChanges
      .pipe(
        tap((value) => {
          if (value.length === 24) {
            this.selectedSubRouteId = value;
            this.applyFilters("subRoute");
            this.shipToControl.enable();
          } else {
            this.selectedSubRouteId = null;
            this.shipToControl.disable();
          }
        }),
        delay(500)
      )
      .subscribe((value) => {
        if (value.length !== 24) {
          const query = buildQuery<SubRouteAttributes>(
            where("name", Ops.like(value, "i"))
          );
          this.findSubRoutesByQuery(query);
        }
      });
    this.shipToControl.valueChanges.subscribe((value) => {
      if (value.length === 24) {
        this.selectedShipToId = value;
        this.applyFilters("shipTo");
      } else {
        this.selectedShipToId = null;
      }
    });
    this.filteredWarehouses$ = this.warehouseControl.valueChanges.pipe(
      tap((value) => {
        if (value.length === 24) {
          this.materialsList = this.materialsList.map((material) => ({
            ...material,
            warehouseId: value,
          }));
        } else {
          this.materialsList = this.materialsList.map((material) => ({
            ...material,
            warehouseId: null,
          }));
        }
      }),
      startWith(""),
      map((value) => this._filter(value || "", this.warehousesList, "name"))
    );
    this.paymentConditions$.subscribe((response) => {
      this.paymentConditionsList = response;
    });
    this.paymentConditionControl.valueChanges
      .pipe(
        tap((value) => {
          this.selectedPaymentConditionId = value.length === 24 ? value : null;
        }),
        delay(500)
      )
      .subscribe((value) => {
        if (value.length !== 24) {
          const query = buildQuery<PaymentConditionAttributes>(
            where("name", Ops.like(value, "i")),
            andWhere("statusId", Ops.eq(StatusEnum.ACTIVE))
          );
          this.findPaymentConditionByQuery(query);
        }
      });
    this.salesForces$.subscribe((response) => {
      this.salesForcesList = response;
    });
    this.salesForceControl.valueChanges
      .pipe(
        tap((value) => {
          this.selectedSalesForceId = value.length === 24 ? value : null;
        }),
        delay(500)
      )
      .subscribe((value) => {
        if (value.length !== 24) {
          const query = buildQuery<SalesOfficeAttributes>(
            where("name", Ops.like(value, "i")),
            andWhere("statusId", Ops.eq(StatusEnum.ACTIVE))
          );
          this.findSalesForceByQuery(query);
        }
      });
    //#endregion
  }

  //#region Fetch and Filter Functions
  _filter(value: string, items: any[], searchKey: string): any[] {
    const filterValue = value.toLowerCase();
    return items.filter((option) =>
      option[searchKey].toLowerCase().includes(filterValue)
    );
  }

  findSalesOfficeByQuery = (
    salesOfficeQuery?: CombinedFilter<SalesOfficeAttributes>
  ) => {
    this.salesOffices$ = this.ordersCreationService
      .fetchSalesOffices(salesOfficeQuery)
      .pipe(
        tap(
          () =>
            (this.filteredDocumentClasses$ =
              this.orderTypeControl.valueChanges.pipe(
                startWith(""),
                map((value) =>
                  this._filter(value || "", this.documentClassesList, "name")
                )
              ))
        ),
        map((response) => response.data)
      );
  };

  findSalesForceByQuery = (
    salesForceQuery?: CombinedFilter<SalesOfficeAttributes>
  ) => {
    this.salesForces$ = this.ordersCreationService
      .fetchSalesOffices(salesForceQuery)
      .pipe(map((response) => response.data));
  };

  findPaymentConditionByQuery = (
    paymentConditionByQuery?: CombinedFilter<PaymentConditionAttributes>
  ) => {
    this.paymentConditions$ = this.ordersCreationService
      .fetchPaymentConditions(paymentConditionByQuery)
      .pipe(map((response) => response.data));
  };

  findCurrenciesByQuery = (
    currencyQuery?: CombinedFilter<CurrencyAttributes>
  ) => {
    this.ordersCreationService
      .fetchCurrencies(currencyQuery)
      .pipe(
        tap(
          () =>
            (this.filteredCurrencies$ = this.currencyControl.valueChanges.pipe(
              startWith(""),
              map((value) =>
                this._filter(value || "", this.currenciesList, "name")
              )
            ))
        )
      )
      .subscribe((response) => {
        this.currenciesList = response.data;
      });
  };

  findDocumentClassesByQuery = (
    documentClassQuery?: CombinedFilter<DocumentClassAttributes>
  ) => {
    this.ordersCreationService
      .fetchDocumentClasses(documentClassQuery)
      .subscribe((response) => {
        this.documentClassesList = response.data;
      });
  };

  findRoutesByQuery = (routeQuery?: CombinedFilter<RouteAttributes>) => {
    this.ordersCreationService.fetchRoutes(routeQuery).subscribe((response) => {
      this.routesList = response.data;
    });
  };

  findSelectedCompanyById = (companyId: string) => {
    this.ordersCreationService
      .fetchCompanyById(companyId)
      .subscribe((response) => {
        this.selectedCompanyBySalesOffice = response.data;
        this.currencyControl.setValue(
          this.selectedCompanyBySalesOffice.currencyBase._id.toString()
        );
        const foundPaymentCondition = this.paymentConditionsList.find(
          (paymentCondition) =>
            paymentCondition?.company?._id?.toString() === companyId
        );
        foundPaymentCondition &&
          this.paymentConditionControl.setValue(
            foundPaymentCondition._id.toString()
          );
      });
  };

  findSubRoutesByQuery = (
    subRouteQuery?: CombinedFilter<SubRouteAttributes>
  ) => {
    this.subRoutesList$ = this.ordersCreationService
      .fetchSubRoutes(subRouteQuery)
      .pipe(map((response) => response.data));
    this.subRoutesList$.subscribe((response) => {
      this.subRoutesList = response;
    });
  };

  findSoldToBySubRoute = (selectedSubRoute: SubRouteAttributes) => {
    /* this.ordersCreationService.fetchSalesOfficeById(selectedSubRoute.salesOffice._id.toString())
      .pipe(
        concatMap(({ data }) => {
          const businessPartnerQuery = buildQuery<BusinessPartnerAttributes & { 'distributionChains._id'?}>(
            where('distributionChains._id', Ops.eq(data.distributionchain._id.toString())),
          );
          return this.ordersCreationService.fetchBusinessPartners(businessPartnerQuery);
        })
      ) .... this continues until the .subscribe(businessPartners) */
    const businessPartnerQuery = buildQuery<
      BusinessPartnerAttributes & { "distributionChains._id"? }
    >(
      where(
        "distributionChains._id",
        Ops.eq(this.selectedSalesOffice.distributionchain._id.toString())
      ),
      andWhere("statusId", Ops.eq(StatusEnum.ACTIVE))
    );
    this.ordersCreationService
      .fetchBusinessPartners(businessPartnerQuery)
      .subscribe((businessPartners) => {
        this.soldToList = businessPartners.data;
        this.shipToList = this.soldToList.reduce(
          (acc: BusinessPartnerAttributes[], currSoldTo) => {
            let shipToToPush = this.findShipToBySoldTo(currSoldTo);
            shipToToPush = shipToToPush.map((shipTo) => ({
              ...shipTo,
              soldToId: currSoldTo._id,
            }));
            acc.push(...shipToToPush);
            return acc;
          },
          []
        );
      });
  };

  findShipToBySoldTo = (
    soldTo: BusinessPartnerAttributes
  ): BusinessPartnerAttributes[] => {
    const foundDistChain = soldTo.distributionChains.find(
      (distChain) =>
        distChain._id.toString() ===
        this.selectedSalesOffice.distributionchain._id.toString()
    );
    const shipToListToReturn: BusinessPartnerAttributes[] = [];
    if (foundDistChain) {
      const reducedShipToList: BusinessPartnerAttributes[] =
        foundDistChain.interlocutors.reduce(
          (
            accShipToList: BusinessPartnerAttributes[],
            currInter: BusinessPartnerInterlocutorAttributes
          ) => {
            if (
              currInter.businessPartnerFunctions.some(
                (bsnPartFnc) => bsnPartFnc.code === ("EM" as any)
              )
            ) {
              accShipToList.push(
                currInter.businessPartner as any as BusinessPartnerAttributes
              );
            }
            return accShipToList;
          },
          []
        );
      shipToListToReturn.push(...reducedShipToList);
    }
    return shipToListToReturn;
  };

  findMaterialsWithCount(): void {
    const pageIndex = this.paginator.pageIndex + 1;
    const pageSize = this.paginator.pageSize;
    const sortDirection =
      this.sort.direction.toUpperCase() === "ASC" ? "ASC" : "DESC";
    const validSortFields: (keyof MaterialAttributes)[] = [
      "code",
      "name",
      "statusId",
    ];
    const sortField: keyof MaterialAttributes = validSortFields.includes(
      this.sort.active as keyof MaterialAttributes
    )
      ? (this.sort.active as keyof MaterialAttributes)
      : "code";

    let selectedSubRoute: SubRouteAttributes = null;
    if (this.selectedSubRouteId !== null) {
      selectedSubRoute = this.subRoutesList.find(
        (l) => l._id.toString() == this.selectedSubRouteId
      );
    }
    const query = buildQuery<
      MaterialAttributes & { "company._id"?; "division._id"? }
    >(
      this.selectedSubRouteId && selectedSubRoute
        ? where(
            "company._id",
            Ops.eq(selectedSubRoute.company._id, Normalizers.ObjectId)
          )
        : noop(),
      this.selectedSalesOffice
        ? where(
            "division._id",
            Ops.in(
              ...this.selectedSalesOffice.divisions.map((division) =>
                division._id.toString()
              ),
              Normalizers.ObjectId
            )
          )
        : noop(),
      this.searchQuery
        ? where("name", Ops.like(this.searchQuery, "i"))
        : noop(),
      andWhere("statusId", Ops.eq(StatusEnum.ACTIVE)),
      andWhere("_deleted", Ops.not(true)),
      orderBy(
        sortField ?? "code",
        sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(pageIndex ?? 1, pageSize ?? 100)
    );
    let fieldsData: FetchSequenceMaterialsFieldsRequest = null;
    if (
      this.selectedSoldToId !== "none" &&
      this.selectedShipToId !== "none" &&
      this.selectedSalesOffice
    ) {
      const selectedSoldTo = this.soldToList.find(
        (l) => l._id.toString() == this.selectedSoldToId
      );
      const selectedShipTo = this.shipToList.find(
        (l) => l._id.toString() == this.selectedShipToId
      );
      fieldsData = {
        field1: this.selectedSalesOffice.externalKeys.code, // 'Organización de ventas'
        field2: selectedSoldTo.externalKeys.code, // 'Cliente'
        field3: selectedShipTo?._id.toString(), // 'Destinatario de mercancía'
        field4: "VB", // 'Lista de precios'
      };
    }
    this.ordersCreationService
      .fetchMaterialsWithCount(query, fieldsData, this.materialsUMSelection)
      .subscribe((data) => {
        const prevMaterialsList = [...this.materialsList];
        this.materialsList = data?.data?.[0]?.data ?? [];
        this.materialsList = this.materialsList.map((material) => ({
          ...this.mapMaterialForList(material, prevMaterialsList),
        }));
        this.totalItems = data?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.paginator.length = this.totalItems;
      });
  }

  mapMaterialForList = (
    material: MaterialAttributes & { quantity?: number; subtotal?: number },
    prevMaterialsList: MaterialExt[]
  ): MaterialExt => {
    const foundMaterial = prevMaterialsList.find(
      (prevMat) => prevMat._id.toString() === material._id.toString()
    );
    return {
      ...material,
      subtotal: foundMaterial ? foundMaterial.subtotal : 0,
      quantity: foundMaterial ? foundMaterial.quantity : 0,
      selectedMeasurement: foundMaterial
        ? foundMaterial.selectedMeasurement
        : material.baseUnitOfMeasure._id.toString(),
      warehouseId: foundMaterial ? foundMaterial.warehouseId : null,
    };
  };

  filterOrdersToConfirm = (orders: {
    valid: Partial<OrderAttributes & any>[];
    invalid: Partial<OrderAttributes & any>[];
  }) => {
    this.ordersFiltered.valid = orders.valid.filter((order) =>
      Boolean(!order.deliveryDateTimeSuggested)
    );
    this.ordersFiltered.valid = this.ordersFiltered.valid.map((order) => ({
      ...order,
      collapsed: false,
    }));
    this.ordersFiltered.toVerify = orders.valid.filter((order) =>
      Boolean(order.deliveryDateTimeSuggested)
    );
    this.ordersFiltered.toVerify = this.ordersFiltered.toVerify.map(
      (order) => ({ ...order, collapsed: false })
    );
    this.ordersFiltered.invalid = orders.invalid;
    this.ordersFiltered.invalid = this.ordersFiltered.invalid.map((order) => ({
      ...order,
      collapsed: false,
    }));
  };

  setupPaginationAndSorting(): void {
    this.paginator.page.subscribe(() => this.findMaterialsWithCount());
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.findMaterialsWithCount();
    });
  }
  //#endregion

  //#region Funciones de materiales - Materials functions
  onQuantityChange(
    event: Event,
    material: MaterialAttributes & { quantity?: number; subtotal?: number }
  ): void {
    const input = event.target as HTMLInputElement;
    const quantityValue: number = +input.value;
    material.quantity = quantityValue;
    material.subtotal = material.price * quantityValue;
  }

  measurementUnitSelected(
    event: any,
    material: MaterialAttributes & { selectedMeasurement }
  ): void {
    const selectedMeasurement: string = event.value;
    material.selectedMeasurement = selectedMeasurement;
    const foundSelectedUM = material.alternalUnitOfMeasures.find(
      (altUM) => altUM.unitOfMeasure._id.toString() === selectedMeasurement
    );
    const materialUMSelectedFound = this.materialsUMSelection.find(
      (umSelected) => umSelected.materialId === material._id.toString()
    );
    if (materialUMSelectedFound) {
      this.materialsUMSelection = this.materialsUMSelection.filter(
        (umSelected) => umSelected.materialId !== material._id.toString()
      );
    }
    this.materialsUMSelection.push({
      materialId: material._id.toString(),
      umId: selectedMeasurement,
      umCode: foundSelectedUM?.unitOfMeasure?.code,
    });
    this.findMaterialsWithCount();
  }

  fetchUMPConversion(
    material: MaterialAttributes & {
      selectedMeasurement: string;
      quantity: number;
    }
  ): string {
    let conversionResult = 0;
    const umToMapFound = material.alternalUnitOfMeasures.find(
      (ump) => ump.unitOfMeasure._id.toString() !== material.selectedMeasurement
    );
    const selectedUMFound = material.alternalUnitOfMeasures.find(
      (ump) => ump.unitOfMeasure._id.toString() === material.selectedMeasurement
    );
    if (!umToMapFound) {
      return (
        material.quantity + " " + material.baseUnitOfMeasure.name + " (Sin UMP)"
      );
    }
    if (umToMapFound.isUMP) {
      const quantityToUMP = material.quantity * umToMapFound.quantity;
      conversionResult =
        quantityToUMP > 0
          ? +(+quantityToUMP / +umToMapFound.equivalentQuantity)
          : 0;
    } else if (umToMapFound.isUMB && selectedUMFound.isUMP) {
      const quantityToUMB = material.quantity * umToMapFound.quantity;
      conversionResult =
        quantityToUMB > 0
          ? +(+selectedUMFound.equivalentQuantity * +quantityToUMB)
          : 0;
    }
    return conversionResult.toFixed(2) + " " + umToMapFound.unitOfMeasure.name;
  }
  //#endregion

  //re-renderizar unicamente las filas con cambios, evita efectos visuales innecesarios
  trackByFn(index: number, item: MaterialAttributes): string {
    return item._id.toString();
  }

  createOrderRequest(): void {
    this.ordersCreationService
      .validateOrderRequest(this.currentOrderRequest)
      .subscribe(
        () => {
          this.findMaterialsWithCount();
          this._snackBar.open("Orden creada exitosamente", "Cerrar");
        },
        (error) => {
          this._snackBar.open(
            "Error creando la orden" + error.message,
            "Cerrar"
          );
        }
      );
  }

  openCreateModal(): void {
    this.orderedMaterials = this.materialsList.filter(
      (material) => material.quantity
    );
    const selectedSubRoute: SubRouteAttributes & { distributionChain? } =
      this.subRoutesList.find(
        (l) => l._id.toString() == this.selectedSubRouteId
      );
    const selectedSoldTo = this.soldToList.find(
      (l) => l._id.toString() == this.selectedSoldToId
    );
    const selectedShipTo = this.soldToList.find(
      (l) => l._id.toString() == this.selectedShipToId
    );
    this.orderedMaterials.forEach((mat) => {
      this.currentTotal += mat.subtotal;
      this.currentSubTotal += mat.subtotal;
    });
    const orderToPush: Partial<OrderAttributes & any> = {
      //#region VERIFY ORIGIN
      code: Math.floor(100000 + Math.random() * 900000),
      type: {
        _id: "66eb01db9a3c319653a134c4",
        code: DocumentTypeEnum.PEDIDO,
        name: "PEDIDO",
        statusId: StatusEnum.ACTIVE,
        _deleted: false,
        createdAt: "2024-09-18T16:37:47.156Z",
        updatedAt: "2024-09-18T16:37:47.156Z",
        __v: 0,
      } as any,
      class: {
        _id: "66edf01fcd7eece358c9d82b",
        code: "PED-001",
        name: "PEDIDO",
        documentType: {
          _id: "66eb01db9a3c319653a134c4",
          code: DocumentTypeEnum.PEDIDO,
          name: "PEDIDO",
          statusId: StatusEnum.ACTIVE,
        },
        statusId: StatusEnum.ACTIVE,
        _deleted: false,
        createdAt: "2024-09-20T21:58:55.869Z",
        updatedAt: "2024-09-20T21:58:55.869Z",
        __v: 0,
      } as any,
      //#endregion
      distributionChain: {
        ...(this.selectedSalesOffice.distributionchain as any),
        company: this.selectedSalesOffice.distributionchain.company.id,
      },
      salesOffice: this.selectedSalesOffice,
      route: this.selectedRoute,
      subRoute: selectedSubRoute,
      soldTo: {
        ...(selectedSoldTo as any),
        ownDiscountList: {
          _id: "66e60b548210c1ff5998fcee",
          code: "23",
          name: "Precio Descuento",
          statusId: StatusEnum.ACTIVE,
        },
        ownPriceList: {
          _id: "66e60b9e8210c1ff5998fcf3",
          code: "24",
          name: "Precio Normal",
          statusId: StatusEnum.ACTIVE,
        },
        address: {
          ...selectedSoldTo.addresses,
          type: {
            ...selectedSoldTo.addresses[0].type,
            type: {
              _id: "6703775d81c90f7346bd29ce",
              id: 2,
              name: "Tipo 2",
              type: "Urbana",
              statusId: StatusEnum.ACTIVE,
            },
          },
        },
      },
      shipTo: {
        ...(selectedShipTo as any),
        ownDiscountList: {
          _id: "66e60b548210c1ff5998fcee",
          code: "23",
          name: "Precio Descuento",
          statusId: StatusEnum.ACTIVE,
        },
        ownPriceList: {
          _id: "66e60b9e8210c1ff5998fcf3",
          code: "24",
          name: "Precio Normal",
          statusId: StatusEnum.ACTIVE,
        },
        address: {
          ...selectedShipTo.addresses[0],
          type: {
            ...selectedShipTo.addresses[0].type,
            type: {
              _id: "6703775d81c90f7346bd29ce",
              id: 2,
              name: "Tipo 2",
              type: "Urbana",
              statusId: StatusEnum.ACTIVE,
            },
          },
        },
      },
      trip: {
        _id: "66eb01db9a3c319653a134c4",
        id: 2,
        dateHourStart: new Date(),
        dateHourEnd: new Date(),
      } as any,
      isOrderTaxExempt: true,
      totalTax: this.currentTaxTotal,
      totalSurcharges: 0,
      totalDiscount: 0,
      total: this.currentTotal,
      deliveryDateTime: new Date(),
      loadDateTime: new Date(),
      creationDateTime: new Date(),
      status: "requested",
      currency: {
        _id: "66de46736be9008e63ecc023" as any,
        name: "Lempiras",
        factor: 1,
      },
      company: {
        ...selectedSubRoute.company,
        id: selectedSubRoute.company.id + 1,
      },
      serial: "serial example",
      detail: this.orderedMaterials.map((material) => ({
        ...(material as any),
        status: "requested",
        note: "Some note",
        totalTax: this.currentTaxTotal,
        totalSurcharges: 0,
        totalDiscount: 0,
        promotionData: {
          code: "promo_code",
          companyId: 2,
          material: material,
          unitOfMeasure: {
            _id: "66e9fa14aac6881544177225",
            code: "456A",
            name: "Libras",
          },
          quantity: material.quantity,
          bonifications: [
            {
              material: material,
              unitOfMeaure: {
                _id: "66e9fa14aac6881544177225",
                code: "456A",
                name: "Libras",
              },
              quantity: material.quantity,
              typeCoverageValue: "coverage value",
              coverageValue: 2,
            },
          ],
          dateTimeStart: new Date(),
          dateTimeEnd: new Date(),
        },
        isPromotion: false,
        hasScalePrices: false,
        hasMinimumAndMaximunPrices: false,
        unitPrice: material.price,
        unitPriceOriginal: material.price,
        originalQuantity: material.quantity,
        conversionFactor: 1,
        unitOfMeasure: {
          _id: "66e9fa5faac688154417722f",
          code: "Kg",
          name: "Kilogramos",
        },
        material: {
          _id: material._id,
          code: material.code,
          externalKeys: material.externalKeys,
          materialGroups: material.materialGroups.map((material) => ({
            ...material,
            id: 1,
          })),
          name: material.name,
        },
      })),
    };
    this.currentOrderRequest.orders.push(orderToPush);
    const dialogRef = this.dialog.open(this.createOrderModal, {
      panelClass: "w-4/5",
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.createOrderRequest();
      }
    });
  }

  openOrdersVerificationModal = () => {
    const dialogRef = this.dialog.open(this.orderVerificationModal, {
      panelClass: "w-4/5",
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {});
  };

  applyFilters(type?: string) {
    switch (type) {
      case "salesOffice":
        if (this.selectedSalesOfficeId === null) {
          this.selectedSalesOffice = null;
          this.selectedSalesOfficeId = null;
        } else {
          this.selectedSalesOffice = this.salesOfficesList.find(
            (l) => l._id.toString() == this.selectedSalesOfficeId
          );
          // this.warehousesList = this.selectedSalesOffice.warehouses || [];
          const subRouteQuery = buildQuery<
            SubRouteAttributes & { "salesOffice._id"? }
          >(
            where(
              "salesOffice._id",
              Ops.eq(
                this.selectedSalesOffice._id.toString(),
                Normalizers.ObjectId
              )
            ),
            andWhere("statusId", Ops.eq(StatusEnum.ACTIVE))
          );
          this.findSubRoutesByQuery(subRouteQuery);
          const routeQuery = buildQuery<RouteAttributes & { "company._id"? }>(
            where(
              "company._id",
              Ops.eq(
                this.selectedSalesOffice?.company?._id?.toString(),
                Normalizers.ObjectId
              )
            ),
            andWhere("statusId", Ops.eq(StatusEnum.ACTIVE))
          );
          this.findRoutesByQuery(routeQuery);
          this.findSelectedCompanyById(
            this.selectedSalesOffice?.company?._id?.toString()
          );
        }
        break;
      case "subRoute":
        let selectedSubRoute: SubRouteAttributes = null;
        if (this.selectedSubRouteId === null) {
          selectedSubRoute = null;
          this.selectedSubRouteId = null;
          this.selectedRouteId = null;
          this.selectedRoute = null;
        } else {
          selectedSubRoute = this.subRoutesList.find(
            (l) => l._id.toString() == this.selectedSubRouteId
          );
          // if (selectedSubRoute.route) {
          //   this.selectedRouteId = selectedSubRoute.route._id.toString();
          //   this.selectedRoute = this.routesList.find(l => (l._id.toString() == this.selectedRouteId));
          // }
          this.findSoldToBySubRoute(selectedSubRoute);
        }
        break;
      case "shipTo":
        if (this.selectedShipToId === "none") {
          this.selectedShipToBP = null;
        } else {
          this.selectedShipToBP = this.shipToList.find(
            (l) => l._id.toString() == this.selectedShipToId
          );
          this.selectedSoldToId = this.selectedShipToBP.soldToId;
          this.selectedSoldToBP = this.soldToList.find(
            (l) => l._id.toString() == this.selectedSoldToId
          );
        }
        break;
    }
    this.findMaterialsWithCount();
  }

  //#region Boilerplate
  fetchSalesOfficeName = (salesOfficeId: string): string => {
    const foundSalesOffice = this.salesOfficesList.find(
      (salesOffice) => salesOffice._id.toString() === salesOfficeId
    );
    if (foundSalesOffice) {
      return foundSalesOffice.name;
    }
    return "";
  };

  fetchOrderTypeName = (documentClassId: string): string => {
    const foundDocumentClass = this.documentClassesList.find(
      (documentClass) => documentClass._id.toString() === documentClassId
    );
    if (foundDocumentClass) {
      return foundDocumentClass.name;
    }
    return "";
  };

  fetchSubRouteName = (subRouteId: string): string => {
    const foundSubRoute = this.subRoutesList.find(
      (subRoute) => subRoute._id.toString() === subRouteId
    );
    if (foundSubRoute) {
      return foundSubRoute.name;
    }
    return "";
  };

  fetchWarehouseName = (warehouseId: string): string => {
    const foundWarehouse = this.warehousesList.find(
      (warehouse) => warehouse._id.toString() === warehouseId
    );
    if (foundWarehouse) {
      return foundWarehouse.name;
    }
    return "";
  };

  fetchShipToName = (shipToId: string): string => {
    const foundShipTo = this.shipToList.find(
      (shipTo) => shipTo._id.toString() === shipToId
    );
    if (foundShipTo) {
      return foundShipTo.name;
    }
    return "";
  };

  fetchCurrencyName = (currencyId: string): string => {
    const foundCurrency = this.currenciesList.find(
      (currency) => currency._id.toString() === currencyId
    );
    if (foundCurrency) {
      return foundCurrency.name;
    }
    return "";
  };

  fetchPaymentConditionName = (paymentConditionId: string): string => {
    const foundPaymentCondition = this.paymentConditionsList.find(
      (paymentCondition) =>
        paymentCondition._id.toString() === paymentConditionId
    );
    if (foundPaymentCondition) {
      return foundPaymentCondition.name;
    }
    return "";
  };

  fetchSalesForceName = (salesForceId: string): string => {
    const foundSalesForce = this.salesForcesList.find(
      (salesForce) => salesForce._id.toString() === salesForceId
    );
    if (foundSalesForce) {
      return foundSalesForce.name;
    }
    return "";
  };

  //TODO Pending to implement instead of all of above. Pending documentation for mat-auto-complete
  fetchShowValue = (
    entityId: string,
    entitiesList: any[],
    keyToShow: string
  ) => {
    const foundEntity = entitiesList.find(
      (shipTo) => shipTo[keyToShow].toString() === entityId
    );
    if (foundEntity) {
      return foundEntity.name;
    }
    return "";
  };
  //#endregion

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value.trim();
    if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
      this.paginator.pageIndex = 0;
      this.findMaterialsWithCount();
    }
  }
}
